import { z } from "zod";

/** Inpatient Phase 2 — nursing notes, periodic vitals, intake/output. */

// ===== Nursing notes =====
export const nursingNoteApiSchema = z.object({
	id: z.string().uuid(),
	admission_id: z.string().uuid(),
	note: z.string(),
	recorded_by_member_id: z.string().uuid().nullable(),
	recorded_at: z.string().datetime(),
});
export type NursingNoteApi = z.infer<typeof nursingNoteApiSchema>;

export const createNursingNoteInputSchema = z.object({
	admission_id: z.string().uuid(),
	note: z.string().min(1).max(4000),
});
export type CreateNursingNoteInput = z.infer<
	typeof createNursingNoteInputSchema
>;

// ===== Inpatient vitals =====
export const inpatientVitalApiSchema = z.object({
	id: z.string().uuid(),
	admission_id: z.string().uuid(),
	recorded_at: z.string().datetime(),
	temp_c: z.number().nullable(),
	pulse: z.number().int().nullable(),
	resp_rate: z.number().int().nullable(),
	bp_systolic: z.number().int().nullable(),
	bp_diastolic: z.number().int().nullable(),
	spo2: z.number().int().nullable(),
	pain_score: z.number().int().nullable(),
	recorded_by_member_id: z.string().uuid().nullable(),
	/** Derived (mean arterial pressure) when both BP values present. */
	map: z.number().nullable().optional(),
});
export type InpatientVitalApi = z.infer<typeof inpatientVitalApiSchema>;

const intIn = (min: number, max: number) =>
	z.coerce.number().int().min(min).max(max).nullable().optional();

export const createInpatientVitalInputSchema = z.object({
	admission_id: z.string().uuid(),
	temp_c: z.coerce.number().min(25).max(45).nullable().optional(),
	pulse: intIn(20, 300),
	resp_rate: intIn(4, 80),
	bp_systolic: intIn(40, 300),
	bp_diastolic: intIn(20, 200),
	spo2: intIn(40, 100),
	pain_score: intIn(0, 10),
});
export type CreateInpatientVitalInput = z.infer<
	typeof createInpatientVitalInputSchema
>;

// ===== Intake / output =====
export const ioDirectionSchema = z.enum(["intake", "output"]);
export type IoDirection = z.infer<typeof ioDirectionSchema>;

export const intakeOutputApiSchema = z.object({
	id: z.string().uuid(),
	admission_id: z.string().uuid(),
	recorded_at: z.string().datetime(),
	direction: z.string(),
	category: z.string().nullable(),
	amount_ml: z.number().int(),
	recorded_by_member_id: z.string().uuid().nullable(),
});
export type IntakeOutputApi = z.infer<typeof intakeOutputApiSchema>;

export const createIntakeOutputInputSchema = z.object({
	admission_id: z.string().uuid(),
	direction: ioDirectionSchema,
	category: z.string().max(40).nullable().optional(),
	amount_ml: z.coerce.number().int().min(1).max(20000),
});
export type CreateIntakeOutputInput = z.infer<
	typeof createIntakeOutputInputSchema
>;

/** Net fluid balance summary for an admission (deterministic sums). */
export const fluidBalanceSchema = z.object({
	total_intake_ml: z.number().int(),
	total_output_ml: z.number().int(),
	net_ml: z.number().int(),
	today_intake_ml: z.number().int(),
	today_output_ml: z.number().int(),
	today_net_ml: z.number().int(),
});
export type FluidBalance = z.infer<typeof fluidBalanceSchema>;

export const intakeOutputListSchema = z.object({
	items: z.array(intakeOutputApiSchema),
	balance: fluidBalanceSchema,
});
export type IntakeOutputList = z.infer<typeof intakeOutputListSchema>;

// ===== Deterministic mean arterial pressure (MAP) =====

/** MAP ≈ DBP + (SBP − DBP)/3 (mmHg). Pure; null if either BP is missing. */
export function computeMap(
	sbp: number | null | undefined,
	dbp: number | null | undefined,
): number | null {
	if (
		sbp == null ||
		dbp == null ||
		!Number.isFinite(sbp) ||
		!Number.isFinite(dbp)
	)
		return null;
	return Math.round(dbp + (sbp - dbp) / 3);
}
